import React, { useState, forwardRef, useImperativeHandle, JSX } from "react";
import {
  Snackbar,
  Alert,
  Slide,
  Box,
  Typography,
  SlideProps,
} from "@mui/material";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import InfoIcon from "@mui/icons-material/Info";
import WarningIcon from "@mui/icons-material/Warning";
export interface CustomSnackbarProps {
  duration?: number;
}
export interface CustomSnackbarRef {
  showSnackbar: (
    message: string,
    severity?: "success" | "error" | "warning" | "info"
  ) => void;
}
const SlideTransition = (props: SlideProps) => {
  return <Slide {...props} direction="left" />;
};

const iconMap: Record<"success" | "error" | "warning" | "info", JSX.Element> = {
  success: <CheckCircleIcon sx={{ mr: 1 }} />,
  error: <ErrorIcon sx={{ mr: 1 }} />,
  info: <InfoIcon sx={{ mr: 1 }} />,
  warning: <WarningIcon sx={{ mr: 1 }} />,
};

const CustomSnackbar = forwardRef<CustomSnackbarRef, CustomSnackbarProps>(
  ({ duration = 3000 }, ref) => {
    const [snack, setSnack] = useState<{
      open: boolean;
      message: string;
      severity: "success" | "error" | "warning" | "info";
    }>({
      open: false,
      message: "",
      severity: "success",
    });

    const showSnackbar = (
      message: string,
      severity: "success" | "error" | "warning" | "info" = "success"
    ) => {
      setSnack({ open: true, message, severity });
    };

    const handleClose: (
      event: React.SyntheticEvent | Event,
      reason?: string
    ) => void = (_, reason) => {
      if (reason === "clickaway") return;
      setSnack((prev) => ({ ...prev, open: false }));
    };

    useImperativeHandle(ref, () => ({ showSnackbar }));

    return (
      <Snackbar
        open={snack.open}
        autoHideDuration={duration}
        onClose={handleClose}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        slots={{
          transition: SlideTransition,
        }}
      >
        <Alert
          onClose={handleClose}
          severity={snack.severity}
          icon={false}
          sx={{
            backgroundColor:
              snack.severity === "success"
                ? "#4caf50"
                : snack.severity === "error"
                ? "#f44336"
                : snack.severity === "warning"
                ? "#ff9800"
                : "#2196f3",
            color: "#fff",
            fontSize: "16px",
            fontWeight: 500,
            borderRadius: "12px",
            boxShadow: "0px 4px 12px rgba(0,0,0,0.3)",
            padding: "12px 20px",
            display: "flex",
            alignItems: "center",
          }}
        >
          <Box display="flex" alignItems="center">
            {iconMap[snack.severity]}
            <Typography variant="body1" sx={{ color: "#fff" }}>
              {snack.message}
            </Typography>
          </Box>
        </Alert>
      </Snackbar>
    );
  }
);

export default CustomSnackbar;
